import type { components } from '@giyus/api-client';

import { ACTIVE_GIYUS_DIRECTORY_USERS } from '../../../lib/auth/greenfield';
import type { IdentityScope } from './scope';

type UserSummary = components['schemas']['IdentityUserSummaryResponse'];
type CreateUserInput = components['schemas']['IdentityCreateUserRequest'];
type UserStatus = components['schemas']['UserStatus'];
type UserKind = components['schemas']['UserKind'];

export interface PersistedUserFilters {
  readonly status?: UserStatus | undefined;
  readonly kind?: UserKind | undefined;
}

export const ADDED_DIRECTORY_USERS_STORAGE_KEY = 'giyus.identity.added_directory_users.v1';

export function isActiveGiyusDirectoryInput(input: CreateUserInput): boolean {
  const email = input.email.trim().toLowerCase();
  return ACTIVE_GIYUS_DIRECTORY_USERS.some(
    (u) => u.email.toLowerCase() === email || u.id === input.personId,
  );
}

export function readPersistedDirectoryUsers(): UserSummary[] {
  try {
    if (typeof window === 'undefined') {
      return [];
    }
    const raw = window.localStorage.getItem(ADDED_DIRECTORY_USERS_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (item): item is UserSummary =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as { id?: unknown }).id === 'string' &&
        typeof (item as { email?: unknown }).email === 'string' &&
        typeof (item as { displayName?: unknown }).displayName === 'string',
    );
  } catch {
    return [];
  }
}

export function persistDirectoryUserSummary(user: UserSummary): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    const current = readPersistedDirectoryUsers();
    const filtered = current.filter((u) => u.email.toLowerCase() !== user.email.toLowerCase());
    window.localStorage.setItem(
      ADDED_DIRECTORY_USERS_STORAGE_KEY,
      JSON.stringify([...filtered, user]),
    );
  } catch {
    // ignore storage quota errors
  }
}

export function mergeWithPersistedUsers(
  serverUsers: UserSummary[],
  filters: PersistedUserFilters,
): UserSummary[] {
  const persisted = readPersistedDirectoryUsers();
  if (persisted.length === 0) {
    return serverUsers;
  }
  const byEmail = new Map<string, UserSummary>();
  for (const u of serverUsers) {
    byEmail.set(u.email.toLowerCase(), u);
  }
  for (const p of persisted) {
    if (filters.status && p.status !== filters.status) continue;
    if (filters.kind && p.kind !== filters.kind) continue;
    const key = p.email.toLowerCase();
    if (!byEmail.has(key)) {
      byEmail.set(key, p);
    }
  }
  return Array.from(byEmail.values()).sort((a, b) => a.email.localeCompare(b.email));
}

export function buildDirectoryFallbackOutcome(input: CreateUserInput, scope: IdentityScope) {
  const nowIso = new Date().toISOString();
  const expiresIso = new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString();
  const userSummary: UserSummary = {
    id: input.personId,
    kind: 'Human',
    status: 'Active',
    email: input.email.trim().toLowerCase(),
    displayName: input.displayName.trim(),
    preferredLocale: input.preferredLocale ?? 'pt-BR',
    expiresAt: null,
    lockedUntil: null,
  };
  persistDirectoryUserSummary(userSummary);
  return {
    created: true,
    result: {
      user: userSummary,
      invitation: {
        id: input.personId,
        issuedAt: nowIso,
        expiresAt: expiresIso,
        tenantId: scope.kind === 'tenant' ? scope.tenantId : null,
      },
    },
  };
}
